/* OFX collection — lazy-load (infinite scroll) replacing numbered pagination.
   ITEM 9. Progressive enhancement, same philosophy as ofx-coll-price-slider.js:
   the Liquid {% paginate %} + {{ paginate | default_pagination }} markup stays in
   the DOM as the no-JS fallback. When this script initializes it adds .is-live to
   the section root, which hides the numbered pagination via CSS, drops an
   IntersectionObserver sentinel after the grid, and fetches subsequent pages over
   the Section Rendering API, appending only the new product cards.

   Why the Section Rendering API + the rendered "next" link (never hand-built
   params): the next-page URL parsed out of {{ paginate | default_pagination }}
   already carries every active facet + sort param, so filtered/sorted result sets
   lazy-load identically with zero param bookkeeping here. We request that next URL
   with &section_id=<this section id>; the JSON response's value for that id is the
   section's full rendered HTML, from which we lift the grid's product cards and the
   refreshed pagination block (to discover the page-after-next link).

   Appended-card behaviors: in THIS theme the card swatches and size chips are
   presentational only (no click handler in the approved mockup — see
   snippets/ofx-product-card.liquid), the sale badges + image are static Liquid, so
   appended cards are functionally complete as-rendered. The one bound behavior is
   the AJAX add-to-cart + cart drawer, which sections/ofx-header.liquid binds ONCE
   at init (querySelectorAll('[data-ofx-add-form]').forEach), per-element, inside a
   closure with no re-bind hook. Appended forms are therefore NOT picked up by the
   header. To keep drawer fidelity without editing the out-of-scope header, this
   script binds appended add-forms with a minimal interceptor that POSTs to
   /cart/add.js and then clicks the existing header cart trigger so the header's own
   open-and-refresh path renders the drawer. It marks forms data-ofx-ajax="1" (the
   same flag the header uses) so neither binder double-binds the other's forms. */
(function () {
  function initSection(root) {
    if (!root || root.__ofxLazyInit) return;

    var main = root.querySelector('.ofx-coll__main');
    var grid = root.querySelector('.ofx-coll__grid');
    if (!main || !grid) return; // empty state / no products → nothing to lazy-load

    var foot = main.querySelector('.ofx-coll__grid-foot');
    var pagination = foot ? foot.querySelector('.ofx-coll__pagination') : null;

    // Discover the section id from the wrapper id ("ofx-coll-<section.id>").
    var sectionId = (root.id || '').replace(/^ofx-coll-/, '');
    if (!sectionId) return;

    // Read the page= number from an href (default 1 when absent).
    function pageOf(href) {
      if (!href) return null;
      try {
        var u = new URL(href, window.location.origin);
        var p = parseInt(u.searchParams.get('page'), 10);
        return isFinite(p) ? p : 1;
      } catch (e) { return null; }
    }

    // Find the next-page URL from the rendered pagination's "Next" control.
    // Resilient to default_pagination markup variants (rel="next", a .next anchor,
    // or — as a last resort — the anchor whose page= is exactly current+1). Returns
    // null when there is no next page (single page, or the last page), so the loop
    // terminates cleanly. The chosen URL always carries the active facet+sort params
    // because it comes straight from the server-rendered pagination.
    function nextUrlFrom(paginationEl, currentHref) {
      if (!paginationEl) return null;

      var byRel = paginationEl.querySelector('a[rel~="next"]');
      if (byRel && byRel.getAttribute('href')) return byRel.getAttribute('href');

      var byClass = paginationEl.querySelector('a.next, a.pagination__next, .next > a');
      if (byClass && byClass.getAttribute('href')) return byClass.getAttribute('href');

      // Last resort: among all page anchors, pick the one one-greater than current.
      var cur = pageOf(currentHref) || 1;
      var anchors = paginationEl.querySelectorAll('a[href]');
      for (var i = 0; i < anchors.length; i++) {
        if (pageOf(anchors[i].getAttribute('href')) === cur + 1) {
          return anchors[i].getAttribute('href');
        }
      }
      return null;
    }

    var nextUrl = nextUrlFrom(pagination, window.location.href);
    if (!nextUrl) return; // only one page of results → nothing to lazy-load

    root.__ofxLazyInit = true;
    // JS is live → CSS hides the numbered pagination, shows nothing extra until we
    // actually fetch (the loader is appended on demand).
    root.classList.add('is-live');

    // ----- Status (aria-live) + loader nodes -----
    var status = document.createElement('div');
    status.className = 'ofx-coll__lazy-status';
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('role', 'status');

    var loader = document.createElement('div');
    loader.className = 'ofx-coll__lazy-loader';
    loader.setAttribute('hidden', '');
    var spinner = document.createElement('span');
    spinner.className = 'ofx-coll__lazy-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    var loaderText = document.createElement('span');
    loaderText.className = 'ofx-coll__lazy-loading-text';
    loaderText.textContent = root.getAttribute('data-lazy-loading') || '';
    loader.appendChild(spinner);
    loader.appendChild(loaderText);

    // Sentinel sits after the grid; loader + status live in the foot region so the
    // existing results-count line is undisturbed. Reserve no fixed height → the
    // loader is display:none until fetching, so there is no layout shift.
    var sentinel = document.createElement('div');
    sentinel.className = 'ofx-coll__lazy-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');

    grid.insertAdjacentElement('afterend', sentinel);
    main.appendChild(loader);
    main.appendChild(status);

    var appendedText = root.getAttribute('data-lazy-appended') || '';
    var allText = root.getAttribute('data-lazy-all') || '';

    var busy = false;
    var done = false;

    function setLoading(on) {
      if (on) loader.removeAttribute('hidden');
      else loader.setAttribute('hidden', '');
    }

    // Bind an appended add-to-cart form to the AJAX cart drawer WITHOUT touching
    // the out-of-scope header. Mirrors the no-JS→drawer contract: POST /cart/add.js,
    // then click the header cart trigger so the header's open path refreshes + shows
    // the drawer. Falls back to native submit on any failure. Idempotent via the
    // same data-ofx-ajax flag the header sets.
    function bindAppendedAddForm(form) {
      if (!form || form.dataset.ofxAjax === '1') return;
      form.dataset.ofxAjax = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var fd = new FormData(form);
        if (!fd.get('id')) { form.submit(); return; }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        }).then(function () {
          // Reuse the header's own open-and-refresh path.
          var trigger = document.querySelector('[data-ofx-cart-open]');
          if (trigger) trigger.click();
        }).catch(function () { form.submit(); });
      });
    }

    function bindAppended(scope) {
      var forms = scope.querySelectorAll('[data-ofx-add-form]');
      for (var i = 0; i < forms.length; i++) bindAppendedAddForm(forms[i]);
    }

    function announce(count, finished) {
      var msg = '';
      if (count > 0 && appendedText) {
        msg = appendedText.replace('__count__', String(count));
      }
      if (finished && allText) msg = (msg ? msg + ' ' : '') + allText;
      status.textContent = msg;
    }

    // Re-arm: measure the sentinel's position directly instead of waiting for an
    // IntersectionObserver transition. The observer only re-notifies when the
    // sentinel EXITS and then RE-ENTERS the 600px rootMargin zone with rendered
    // frames at both transitions — that contract breaks in two real cases:
    //   (a) an appended page shorter than viewport+600px never pushes the sentinel
    //       out of the zone, so no exit/re-enter ever fires → stall after one page;
    //   (b) intersection changes that happen while the tab is hidden are not
    //       delivered (no frames), and on return the observer's last state is still
    //       "intersecting" → no fresh notification → stall.
    // So after every append (and at initial setup, and on tab re-show) we ask the
    // DOM where the sentinel actually is; if it's within range and we're idle, we
    // self-drive the next fetch. This is additive — the observer still handles
    // ordinary scrolling.
    function maybeFetchByPosition() {
      if (busy || done || !nextUrl) return;
      if (!sentinel || !sentinel.parentNode) return;
      var rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 600) fetchNext();
    }

    function fetchNext() {
      if (busy || done || !nextUrl) return;
      busy = true;
      setLoading(true);

      // Append section_id to the rendered next URL (which already carries every
      // active facet + sort param). Build via URL so an existing query string is
      // preserved and section_id is added/overwritten cleanly.
      var url;
      try {
        url = new URL(nextUrl, window.location.origin);
      } catch (err) {
        url = null;
      }
      if (!url) { busy = false; setLoading(false); return; }
      url.searchParams.set('section_id', sectionId);
      var fetchedHref = nextUrl; // the page we are about to load (for next-of-next)

      fetch(url.toString(), { headers: { 'Accept': 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('.ofx-coll__grid');
          var newCards = newGrid ? newGrid.querySelectorAll('.ofx-coll__card') : [];

          var appendedCount = 0;
          if (newCards.length) {
            var frag = document.createDocumentFragment();
            for (var i = 0; i < newCards.length; i++) {
              frag.appendChild(document.importNode(newCards[i], true));
            }
            grid.appendChild(frag);
            appendedCount = newCards.length;
            // Wire up the freshly appended add-to-cart forms.
            bindAppended(grid);
          }

          // Discover the page-after-next link from the response's pagination.
          var newPagination = doc.querySelector('.ofx-coll__pagination');
          nextUrl = nextUrlFrom(newPagination, fetchedHref);

          if (!nextUrl) {
            done = true;
            announce(appendedCount, true);
            cleanup();
          } else {
            announce(appendedCount, false);
          }
        })
        .catch(function () {
          // Network/parse failure: stop quietly, leave the page usable. Do not
          // spam retries; the no-JS pagination markup is already hidden, but the
          // already-loaded products remain fully functional.
          done = true;
          cleanup();
        })
        .then(function () {
          busy = false;
          setLoading(false);
          // Cures reproduction (a): now that busy is cleared, re-measure the
          // sentinel. If this freshly appended page was too short to push it out of
          // the 600px zone, fetch again — and keep self-driving until the sentinel
          // is genuinely out of range or there is no next page. Deferred to the next
          // frame so layout reflects the newly appended cards before we measure. On
          // the failure path `done` is already true, so this is a no-op there.
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(maybeFetchByPosition);
          } else {
            setTimeout(maybeFetchByPosition, 0);
          }
        });
    }

    // ----- IntersectionObserver sentinel -----
    var observer = null;

    // Cures reproduction (b): intersection transitions that occur while the tab is
    // hidden are never delivered (no frames render), so on return the observer's
    // last-reported state is still "intersecting" and no fresh notification fires.
    // When the tab becomes visible again we re-measure the sentinel directly and
    // self-drive if it's in range. Named so cleanup() can remove it.
    function onVisible() {
      if (document.visibilityState === 'visible') maybeFetchByPosition();
    }

    function cleanup() {
      if (observer) { observer.disconnect(); observer = null; }
      document.removeEventListener('visibilitychange', onVisible);
      if (sentinel && sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { fetchNext(); break; }
        }
      }, { root: null, rootMargin: '600px 0px', threshold: 0 });
      observer.observe(sentinel);

      // Self-drive on tab re-show (reproduction b).
      document.addEventListener('visibilitychange', onVisible);

      // Initial re-arm: if the first rendered page already leaves the sentinel
      // inside the 600px zone (e.g. a tall viewport with a short first page), the
      // observer may report "intersecting" once and never again. Measure directly
      // after setup so the first fetch is self-driven too.
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(maybeFetchByPosition);
      } else {
        setTimeout(maybeFetchByPosition, 0);
      }
    } else {
      // No IO support → restore the numbered pagination (undo is-live) so the user
      // keeps a working path. Remove our sentinel/loader.
      root.classList.remove('is-live');
      cleanup();
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('.ofx-coll');
    for (var i = 0; i < roots.length; i++) initSection(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init after a Shopify section re-render (theme editor / filter re-render):
  // a fresh section root has no __ofxLazyInit flag, so initSection runs clean.
  document.addEventListener('shopify:section:load', initAll);
})();
